import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LogService } from '@innovation-os-ng/core/logger';
import { User } from '@innovation-os-ng/core/models';
import { getDomain, getUrlWithTenantAndDomain } from '@innovation-os-ng/core/utils';
import { jwtDecode } from 'jwt-decode';
import { CookieOptions, CookieService } from 'ngx-cookie';
import { Observable, ReplaySubject, from } from 'rxjs';
import { JwtToken, SessionService } from '../contracts/session-service.contract';
import { TokenService } from '../contracts/token-service.contract';
import { UserHistoryService } from '../contracts/user-history.contract';
import { ApiKeyHeaderInterceptor } from '../interceptors/api-key-header.interceptor';
import { TokenRefreshedResponse } from '../models/ApiResponse';

const COOKIE_NAME_TOKEN: string = `token`;
const COOKIE_NAME_TENANT: string = `tenantUri`;
const COOKIE_NAME_SPACE: string = `spaceUri`;
const COOKIE_NAME_REFRESH_TOKEN: string = `refreshToken`;
const COOKIE_NAME_ACCESS_TOKEN: string = `accessToken`;
const ONE_MINUTE: number = 60 /* s */ * 1; /* min */
const TEN_MINUTES: number = ONE_MINUTE * 10;

@Injectable()
export class CookieSessionService implements SessionService, UserHistoryService {
  public onAuthReady: ReplaySubject<string> = new ReplaySubject<string>(1);

  /**
   * Defines the TLD for which the cookie will be set.
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  private COOKIE_DOMAIN: string;

  private refreshRequest: Promise<TokenRefreshedResponse> = null;

  private autoRefreshTimeout: ReturnType<typeof setTimeout>;
  private refreshInProgress: boolean;
  private pendingRefreshPromise: Promise<boolean>;

  constructor(
    private readonly cookieService: CookieService,
    private readonly tokenService: TokenService,
    private readonly logger: LogService
  ) {
    this.resolveCookieDomain();
  }

  /**
   * Returns the authentication token of the current user.
   */
  public getToken(): string {
    const token = this.cookieService.get(COOKIE_NAME_TOKEN);
    if (token) {
      this.onAuthReady.next(token);
    }
    return token;
  }

  public getDecodedToken(): JwtToken {
    if (!this.getToken()) {
      return null;
    }

    return jwtDecode(this.getToken());
  }

  /**
   * Returns the current user's refresh token.
   */
  public getRefreshToken(): string {
    return this.cookieService.get(COOKIE_NAME_REFRESH_TOKEN);
  }

  /**
   * Returns the current user's access token.
   */
  public getAccessToken(): string {
    return this.cookieService.get(COOKIE_NAME_ACCESS_TOKEN);
  }

  /**
   * Returns the current user's information.
   */
  public getUser(): User {
    if (!this.getToken()) {
      return null;
    }

    const decodeToken = this.getDecodedToken();

    // User can login into the system through normal login and sso,
    // Both of them will provide token with the userUri in the sub property
    // But for the existing user, when we login via sso it will use new user uri from the new user pool
    // Which will cause problem in the permission, so BE will send old user-uri in 'custom:DefaultUserUri' property
    // So we should use this uir as the user-uri, so replace sub(new user-uri) with the old one

    if (decodeToken['custom:DefaultUserUri']) {
      decodeToken.sub = decodeToken['custom:DefaultUserUri'];
    }

    return decodeToken;
  }

  /**
   * Saves the authorization state with a given id token, refresh token and a accessToken
   *
   * @param idToken
   * @param refreshToken
   * @param accessToken
   */
  public storeAuthentication(idToken: string, refreshToken: string, accessToken: string): void {
    this.removeApiKeyCookie(); // When logging in, get rid of the API Key just to be safe...
    this.putCookie(COOKIE_NAME_TOKEN, idToken);
    this.putCookie(COOKIE_NAME_REFRESH_TOKEN, refreshToken);
    this.putCookie(COOKIE_NAME_ACCESS_TOKEN, accessToken);

    this.onAuthReady.next(idToken);

    // Reset the automatic refresh timer and start a new one.
    clearInterval(this.autoRefreshTimeout);
    this.autoRefreshTimeout = null;
    this.scheduleTokenRefresh();
  }

  // TODO all this lastVisited logic in the auth module is a really odd place
  /**
   * Saves the provided tenant slug as last visited
   *
   * @param tenantSlug
   */
  public setLastVisitedTenant(tenantSlug: string): Promise<void> {
    return Promise.resolve(
      this.putCookie(COOKIE_NAME_TENANT, tenantSlug)
    );
  }

  /**
   * Saves the provided space as last visited
   *
   * @param spaceUri
   *
   */
  public setLastVisitedSpace(spaceUri: string): Promise<void> {
    return Promise.resolve(
      this.putCookie(COOKIE_NAME_SPACE, spaceUri)
    );
  }

  /**
   * Returns slug of the last visited tenant
   */
  public getLastVisitedTenant(): Promise<string> {
    return Promise.resolve(this.cookieService.get(COOKIE_NAME_TENANT));
  }

  /**
   * Returns slug of the last visited space
   */
  public getLastVisitedSpaceBySlug(): Promise<string> {
    return Promise.resolve(this.cookieService.get(COOKIE_NAME_SPACE));
  }

  /**
   * Returns the last visited space
   */
  public getLastVisitedSpace(): Promise<string> {
    return Promise.resolve(this.cookieService.get(COOKIE_NAME_SPACE));
  }

  /**
   * Removes the current authorization state, i.e. logs out the user.
   */
  public revokeAuthentication(): void {
    this.removeCookie(COOKIE_NAME_TOKEN);
    this.removeCookie(COOKIE_NAME_REFRESH_TOKEN);
    this.removeCookie(COOKIE_NAME_ACCESS_TOKEN);
  }

  /**
   * Returns whether the current token is expired or not.
   */
  public isTokenExpired(): boolean {
    // If any of the tokens is missing, clean up and bail out
    if (!this.getToken() || !this.getRefreshToken()) {
      return true;
    }
    const token = this.getDecodedToken();
    return token.exp <= Date.now() / 1000;
  }

  /**
   * Returns whether the current user is authenticated based on their existing tokens.
   */
  public isAuthenticated(): Promise<boolean> {
    // If any of the tokens is missing, clean up and bail out
    if (!this.getToken() || !this.getRefreshToken()) {
      this.revokeAuthentication();
      return Promise.resolve(false);
    }

    // Check if a refresh is already in progress
    if (this.pendingRefreshPromise) {
      // If refresh is already in progress, return the pending promise
      return this.pendingRefreshPromise;
    }

    const token: JwtToken = this.getDecodedToken();

    // Token is expired - can we refresh it?
    const isTokenExpired: boolean = token.exp <= Date.now() / 1000;
    if (isTokenExpired) {
      // Create a promise for the refresh operation
      this.pendingRefreshPromise = this.refreshToken()
        .then(() => {
          // Refresh successful, clear the pending promise and return true
          this.pendingRefreshPromise = null;
          return true;
        })
        .catch((error) => {
          // Refresh failed, revoke authentication and clear the pending promise
          this.revokeAuthentication();
          this.pendingRefreshPromise = null;
          return false;
        });

      return this.pendingRefreshPromise;
    }

    // Token is valid, no need to refresh
    return Promise.resolve(true);
  }

  /**
   * Attempts to refresh the currrent ID token using the current refresh token.
   *
   * @throws InvalidTokenException
   * @throws PasswordResetRequiredException
   * @throws UserNotFoundException
   */
  public refreshToken(): Promise<TokenRefreshedResponse> {
    return new Promise((resolve, reject) => {
      if (this.refreshRequest) {
        return this.refreshRequest;
      }

      this.tokenService
        .refreshToken(this.getUser().sub, this.getRefreshToken())
        .then((tokenRefreshResponse) => {
          this.storeAuthentication(tokenRefreshResponse.idToken, this.getRefreshToken(), tokenRefreshResponse.accessToken);
          this.refreshRequest = null;
          resolve(tokenRefreshResponse);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public refreshTokenAsObservable(): Observable<TokenRefreshedResponse> {
    return from(this.refreshToken());
  }

  /**
   * Removes the API key Cookie set by the Teaser application.
   */
  public removeApiKeyCookie(): void {
    this.removeCookie(ApiKeyHeaderInterceptor.API_KEY_COOKIE_KEY, {
      domain: getUrlWithTenantAndDomain(),
      path: '/',
      sameSite: 'none',
      secure: true,
      httpOnly: false
    });
  }

  /**
   * returns the current cookie domain
   *
   * @returns
   */
  public getCookieDomain(): string {
    return this.COOKIE_DOMAIN;
  }

  /**
   * We extract the cookie domain from the hostname
   * to resolve the environment
   *
   * spec: https://itonics.atlassian.net/wiki/spaces/CLOUD/pages/5624778358286024717/CLOUD+Domain+Logic
   */
  private resolveCookieDomain() {
    this.COOKIE_DOMAIN = getDomain();
  }

  /**
   * Schedules a call to refresh the ID token shortly before it will expire.
   *
   * @param epsilon
   *  Defines (in seconds) when the refresh call should be performed before
   *  the token will actually run out. For example, if one minute is specified,
   *  then the refresh call would be made one minute before the token's
   *  actual expiration time.
   */
  private scheduleTokenRefresh(epsilon: number = TEN_MINUTES): void {
    // Ensure that there's only one timer currently running.
    if (this.autoRefreshTimeout) {
      return;
    }

    this.logger.info('New token refresh schedule created');

    const expirationTime = this.getDecodedToken().exp;
    const currentTime = ~~(Date.now() / 1000);

    // If substracting the threshold (epsilon) from the remaining time
    // would result in a negative number, we set the timeout to zero
    // in order to instantly refresh the token.
    const remainingTime = Math.max(0, expirationTime - currentTime - epsilon);

    this.autoRefreshTimeout = setTimeout(async () => {
      try {
        await this.refreshToken();
      } catch (error) {
        // If refreshing the token fails due to a network error,
        // retry again in a bit (half of the epsilon time, but
        // abort if less then one minute is left).
        if (error instanceof HttpErrorResponse) {
          const retryEpsilon = ~~(epsilon / 2);
          if (retryEpsilon >= ONE_MINUTE) {
            this.scheduleTokenRefresh(retryEpsilon);
          }
          return;
        }
      }

      // Reschedule the next token refresh call with the same
      // tolerance threshold.
      this.scheduleTokenRefresh(epsilon);
    }, remainingTime /* s*/ * 1000 /* ms */);
  }

  private putCookie(key: string, value: string, options: CookieOptions = {
    domain: this.COOKIE_DOMAIN,
    path: '/',
    sameSite: 'strict', // our image endpoints and rest api requires cookies in some instances but are different domains
    secure: true, // secure = https only means we need to develop with a https domain or the cookie won't show to JS either
    httpOnly: false // JS aka angular needs to read the cookie and apply it to the bearer/auth-header
  }) {
    this.cookieService.put(key, value, options);
  }

  private removeCookie(key: string, options: CookieOptions = {
    domain: this.COOKIE_DOMAIN,
    path: '/',
    sameSite: 'strict', // our image endpoints and rest api requires cookies in some instances but are different domains
    secure: true, // secure = https only means we need to develop with a https domain or the cookie won't show to JS either
    httpOnly: false // JS aka angular needs to read the cookie and apply it to the bearer/auth-header
  }) {
    this.cookieService.remove(key, options);
  }
}
